import { AddIcon } from "@chakra-ui/icons";
import {
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Box,
    Button,
    Input,
    FormControl,
    Textarea,
    FormErrorMessage,
    useDisclosure,
    Divider,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay
} from "@chakra-ui/react";
import { Formik, FieldArray, Form, useFormikContext, Field, FormikProps } from "formik";
import { cloneDeep } from "lodash";
import debounce from "lodash/debounce";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
// import Editor from "@monaco-editor/react";
import hotToast from "react-hot-toast";
import { BsTrash, BsPlay } from "react-icons/bs";
import { VscTools } from "react-icons/vsc";
import { useDispatch, useSelector } from "react-redux";
import { CellTool, WishedTool } from "../../models/collaborate.model";
import {
    NodeState,
    selectSelectedCell,
    updateSelectedCellToolWishList
} from "../../store/slices/node.slice";
import { selectPersona } from "../../store/slices/persona.slice";
import { setSelectedTestTool } from "../../store/slices/tools.slice";
import { PersonaModel } from "../../types/persona";
import { useHttpClient } from "../../utils/http.utils";
import {
    convertObjectKeyToLabel,
    getPersonaFullName,
    getPersonaProfilePicture
} from "../../utils/string";
import FormikSelect from "../form/FormikSelect";
import PersonaSelectOption from "../persona/PersonaSelectOption";
import FormikFormInputControl from "../tools/FormikFormInputControl";
import ToolWishListOutput from "../tools/ToolWishListOutput";
import ToolsLabControlPanel from "./ToolsLabControlPanel";

type ToolsArg = {
    tool_id: string;
    tool_name: string;
    display_name: string;
    args: {
        key: string;
        label: string;
        required: boolean;
        type: string;
        value: string;
    }[];
};

const ToolsControlPanel: React.FC = () => {
    // Get tools from the react state
    const nodesDataState: NodeState = useSelector((state: any) => state.nodeData);
    const selectedCell = useSelector(selectSelectedCell);
    const toolTestDisc = useDisclosure();
    const selectPersonaDisc = useDisclosure();
    const toolWishListTestDisc = useDisclosure();
    const dispatch = useDispatch();
    const wishedToolFormRef = useRef<FormikProps<any>>(null);
    const selectedToolArgumentsFormRef = useRef<FormikProps<any>>(null);
    const toolsFallbackArgumentsFormRef = useRef<FormikProps<any>>(null);
    const [index, setIndex] = useState<number[]>([]); // No panels open by default
    const [outputData, setOutputData] = useState<{
        file: string;
        text: string | { complete_output: string; system_arguments: any };
    }>(); // No panels open by default
    const { executeToolWishlist } = useHttpClient();
    const [isExecutingToolWishList, setIsExecutingToolWishList] = useState(false);
    const formikRef = useRef<FormikProps<any>>(null);
    const personas = useSelector(selectPersona);

    const handleAccordionToggle = (index) => {
        setIndex(index);
    };

    // const openAccordionPanel = (panelIndex: number) => {
    //     setIndex([panelIndex]);
    // };

    const selectedToolWishList = useMemo(() => {
        return selectedCell?.tool_wishlist.find(
            (tw) => tw.tool_identifier === nodesDataState.selectedWishedToolId
        );
    }, [selectedCell?.tool_wishlist, nodesDataState.selectedWishedToolId]);

    /**
     * Reset forms
     */
    useEffect(() => {
        if (selectedToolArgumentsFormRef?.current) {
            selectedToolArgumentsFormRef?.current.resetForm();
        }

        if (toolsFallbackArgumentsFormRef?.current) {
            toolsFallbackArgumentsFormRef?.current.resetForm();
        }
    }, [selectedToolWishList?.tool_identifier]);

    const toolOptions = useMemo(() => {
        return nodesDataState.tools.map((tool) => ({
            label: tool.display_name || tool.tool_name,
            value: tool.tool_id
        }));
    }, [nodesDataState.tools]);

    const argumentForm = useMemo(() => {
        const arg = selectedToolWishList?.selected_tool.arguments_object;
        if (arg) {
            const keys = Object.keys(arg);
            /**
             * Show only arguments parameter_type equals custom or normal
             */
            return keys
                .filter((key) => ["custom", "normal", "user"].includes(arg[key].parameter_type))
                .map((key) => ({
                    key,
                    description: arg[key].description,
                    required: !!arg[key].required,
                    label: convertObjectKeyToLabel(key),
                    type: arg[key].type,
                    value: arg[key].default_value || "",
                    parameter_type: arg[key].parameter_type,
                    key_placeholder: arg[key].key_placeholder || undefined,
                    button_text: arg[key].button_text || ""
                }));
        } else {
            return [];
        }
    }, [selectedToolWishList?.selected_tool?.arguments_object]);

    const fallbackToolsArgumentForm = useMemo(() => {
        const fallbackTools = selectedToolWishList?.tool_replacements;

        const args: ToolsArg[] = [];

        if (Array.isArray(fallbackTools)) {
            for (const tool of fallbackTools) {
                if (tool) {
                    const arg = tool.arguments_object;
                    const keys = Object.keys(arg);
                    /**
                     * Show only arguments parameter_type equals custom or normal
                     */
                    const toolIndex = args.findIndex((a) => a.tool_id === tool.tool_id);

                    if (toolIndex > -1) {
                        args[toolIndex].args = args[toolIndex].args.concat(
                            keys
                                .filter((key) =>
                                    ["custom", "normal", "user"].includes(arg[key].parameter_type)
                                )
                                .map((key) => ({
                                    key,
                                    label: convertObjectKeyToLabel(key),
                                    description: arg[key].description,
                                    type: arg[key].type,
                                    required: !!arg[key].required,
                                    value: arg[key].default_value || "",
                                    parameter_type: arg[key].parameter_type,
                                    key_placeholder: arg[key].key_placeholder || undefined,
                                    button_text: arg[key].button_text || ""
                                }))
                        );
                    } else {
                        args.push({
                            tool_id: tool.tool_id,
                            tool_name: tool.tool_name,
                            display_name: tool.display_name,
                            args: keys
                                .filter((key) =>
                                    ["custom", "normal", "user"].includes(arg[key].parameter_type)
                                )
                                .map((key) => ({
                                    key,
                                    label: convertObjectKeyToLabel(key),
                                    type: arg[key].type,
                                    description: arg[key].description,
                                    required: !!arg[key].required,
                                    value: arg[key].default_value || "",
                                    parameter_type: arg[key].parameter_type,
                                    key_placeholder: arg[key].key_placeholder || undefined,
                                    button_text: arg[key].button_text || ""
                                }))
                        });
                    }
                }
            }
        }
        return args;
    }, [selectedToolWishList?.tool_replacements]);

    const validateRequired = (value: string, label: string) => {
        let error;

        if (!value) {
            error = `${label} is required`;
        }

        return error;
    };

    /**
     * Test wished tool
     */
    const testWishedTool = async () => {
        const wishedFormErrors = await wishedToolFormRef.current!.validateForm();
        const personaFormErrors = await formikRef.current!.validateForm();
        // const fallbackErrors = await toolsFallbackArgumentsFormRef.current!.validateForm();

        if (Object.keys(wishedFormErrors).length > 0) {
            hotToast.error("Please fill all required fields.");
            return;
        }

        if (Object.keys(personaFormErrors).length > 0) {
            hotToast.error("Please select a persona");
            return;
        }

        // if (Object.keys(selectedToolErrors).length > 0) {
        //     openAccordionPanel(0);
        //     hotToast.error("Please fill all required arguments for the selected tool.");
        //     return;
        // }

        // if (Object.keys(fallbackErrors).length > 0) {
        //     openAccordionPanel(0);
        //     hotToast.error("Please fill all required arguments for the tools fallback.");
        //     return;
        // }

        // close modal
        selectPersonaDisc.onClose();
        /**
         * Execute tools wishlist
         */
        setIsExecutingToolWishList(true);

        executeToolWishlist({
            cellId: selectedCell?.identifier as number,
            toolId: selectedToolWishList?.tool_identifier as number,
            personaId: formikRef.current!.values.persona.value
        })
            .then((data) => {
                setOutputData(data.execution_response.data);
                // open modal
                toolWishListTestDisc.onOpen();
            })
            .catch((err) => {
                console.error(err);
                hotToast.error(
                    `Cannot execute ${selectedToolWishList?.title} at this time. Please try again`
                );
            })
            .finally(() => {
                setIsExecutingToolWishList(false);
            });
    };

    // open tool test drawer
    const testSelectedTool = () => {
        // set selected tool wishlist as test tool
        if (selectedToolWishList) {
            dispatch(setSelectedTestTool(selectedToolWishList.selected_tool as CellTool));
            toolTestDisc.onOpen();
        }
    };

    const personaOptions = useMemo(() => {
        return personas.map((persona: PersonaModel) => ({
            label: getPersonaFullName(persona),
            value: persona.id,
            type: persona.type,
            status: persona.status,
            profile_photo: getPersonaProfilePicture(persona)
        }));
    }, [personas]);

    const persistSelectedToolArgument = (argKey: string, checked: boolean) => {
        const selectedToolCopy = cloneDeep<CellTool>(
            selectedToolWishList?.selected_tool as CellTool
        );

        for (const key of Object.keys(selectedToolCopy.arguments_object)) {
            // check types
            if (key === argKey) {
                if (checked) {
                    selectedToolCopy.arguments_object[key].prev_parameter_type =
                        selectedToolCopy.arguments_object[key].parameter_type;
                    selectedToolCopy.arguments_object[key].parameter_type = "user";
                } else {
                    if (selectedToolCopy.arguments_object[key].prev_parameter_type) {
                        selectedToolCopy.arguments_object[key].parameter_type = selectedToolCopy
                            .arguments_object[key].prev_parameter_type as string;
                    }
                }
            }
        }
        // update state
        dispatch(
            updateSelectedCellToolWishList({
                selected_tool: selectedToolCopy
            })
        );
    };

    const persistFallbackToolArgument = (toolId: string, argKey: string, checked: boolean) => {
        const selectedTestToolCopy: WishedTool = cloneDeep<WishedTool>(
            selectedToolWishList as WishedTool
        );

        const tool_replacements = selectedTestToolCopy.tool_replacements.map((tool) => {
            if (toolId === tool.tool_id) {
                if (checked) {
                    tool.arguments_object[argKey].prev_parameter_type =
                        tool.arguments_object[argKey].parameter_type;
                    tool.arguments_object[argKey].parameter_type = "user";
                } else {
                    if (tool.arguments_object[argKey].prev_parameter_type) {
                        tool.arguments_object[argKey].parameter_type = tool.arguments_object[argKey]
                            .prev_parameter_type as string;
                    }
                }
            }
            return tool;
        });

        dispatch(
            updateSelectedCellToolWishList({
                tool_replacements
            })
        );
    };

    return (
        <div
            id="tools-control-panel"
            className="relative tools-control h-full grid grid-rows-[60px_auto] overflow-hidden w-full"
        >
            <div className="header px-[16px] border-b pb-[8px] grid grid-cols-[280px_75px] items-center">
                <div className="wished-tool-details">
                    <h4 className="text-[15px] font-semibold text-slate-200">
                        {selectedToolWishList?.title || "Wished Tool Title"}
                    </h4>
                    <p className="text-[13px] w-full truncate whitespace-nowrap text-neutral-400">
                        {selectedToolWishList?.description || "Wished Tool Description"}.
                    </p>
                </div>
                <div className="action flex justify-end">
                    <Button
                        onClick={selectPersonaDisc.onOpen}
                        pl={"2px"}
                        py="0px"
                        pr="8px"
                        height={"33px"}
                        background={"transparent"}
                        color={"#22c55e"}
                        fontSize={"14px"}
                        isLoading={isExecutingToolWishList}
                        className="pl-[2px] pr-[8px] py-[2px] rounded border border-green-400 hover:bg-opacity-90 text-green-500 flex"
                    >
                        <BsPlay size="22px" /> Test
                    </Button>
                </div>
            </div>
            <div className="h-full pt-6 overflow-y-auto px-[16px]">
                <Formik
                    innerRef={wishedToolFormRef}
                    initialValues={{
                        title: selectedToolWishList?.title,
                        description: selectedToolWishList?.description,
                        selected_tool: selectedToolWishList?.selected_tool?.tool_id
                            ? {
                                  value: selectedToolWishList?.selected_tool?.tool_id,
                                  label:
                                      selectedToolWishList?.selected_tool?.display_name ||
                                      selectedToolWishList?.selected_tool?.tool_name
                              }
                            : "",
                        tool_replacements: selectedToolWishList?.tool_replacements?.map((tr) => ({
                            value: tr.tool_id,
                            label: tr.display_name || tr.tool_name
                        }))
                    }}
                    enableReinitialize
                    onSubmit={() => {}}
                >
                    {(formik) => (
                        <form onSubmit={formik.handleSubmit}>
                            <FormControl
                                mb={8}
                                isInvalid={!!formik.errors.title && !!formik.touched.title}
                            >
                                <h2 className="text-[14px] text-white mb-2 font-medium">Title</h2>
                                <Field
                                    as={Input}
                                    name="title"
                                    placeholder="Enter title"
                                    variant="filled"
                                    validate={(value) => validateRequired(value, "Title")}
                                />
                                <FormErrorMessage>{String(formik.errors.title)}</FormErrorMessage>
                            </FormControl>

                            <FormControl
                                mb={8}
                                isInvalid={
                                    !!formik.errors.description && !!formik.touched.description
                                }
                            >
                                <h2 className="text-[14px] text-white mb-2 font-medium">
                                    Description
                                </h2>
                                <Field
                                    as={Textarea}
                                    name="description"
                                    placeholder="Enter description"
                                    className="resize-none"
                                    variant="filled"
                                    rows={3}
                                    validate={(value) => validateRequired(value, "Description")}
                                />
                                <FormErrorMessage>
                                    {String(formik.errors.description)}
                                </FormErrorMessage>
                            </FormControl>

                            <FormControl mb={8}>
                                <div className="flex justify-between items-center pb-2">
                                    <p className="text-[14px] text-white mb-2 font-medium">
                                        Selected Tool
                                    </p>
                                    <button
                                        className="pl-[2px] pr-[4px] py-[1px] hover:bg-zinc-700 rounded border-0 font-medium text-green-500 flex"
                                        onClick={testSelectedTool}
                                    >
                                        <BsPlay size="22px" />
                                        Test
                                    </button>
                                </div>
                                <FormikSelect
                                    name="selected_tool"
                                    options={toolOptions}
                                    isSearchable={true}
                                />
                            </FormControl>

                            <FormControl mb={8}>
                                <p className="text-[14px] text-white mb-2 font-medium">
                                    Tools Fallback
                                </p>
                                <FormikSelect
                                    name="tool_replacements"
                                    isSearchable={true}
                                    isMulti={true}
                                    options={toolOptions}
                                />
                            </FormControl>
                            <ToolFormAutoSave nodeState={nodesDataState} debounceMs={500} />
                        </form>
                    )}
                </Formik>

                <Accordion
                    index={index}
                    onChange={handleAccordionToggle}
                    className="control-panel-accordion"
                    allowMultiple
                >
                    <AccordionItem>
                        <AccordionButton className="control-panel-accordion-btn">
                            <Box as="span" flex="1" textAlign="left">
                                <h4 className="text-[15px]">Tool Arguments</h4>
                                <p className="text-[13px] font-normal text-gray-400">
                                    Parameters to be run with tools
                                </p>
                            </Box>
                            <AccordionIcon />
                        </AccordionButton>

                        <AccordionPanel pb={4}>
                            <div className="section-header mt-[10px] pb-[6px] mb-[4px]">
                                <h4 className="text-[15px] font-semibold text-blue-200">
                                    Selected Tool Arguments
                                </h4>
                                {/* <p className="text-[14px] text-neutral-400">Configure the arguments for the fallback tools.</p> */}
                                <Divider className="mt-2" />
                            </div>
                            <Formik
                                innerRef={selectedToolArgumentsFormRef}
                                initialValues={argumentForm.reduce(
                                    (acc, obj) => ({ ...acc, [obj.key]: obj.value }),
                                    {}
                                )}
                                onSubmit={() => {}}
                            >
                                {(formik) => (
                                    <form onSubmit={formik.handleSubmit}>
                                        {argumentForm.map((arg, index) => (
                                            <FormikFormInputControl
                                                key={`arg-control-${index}`}
                                                name={arg.key}
                                                argument={arg}
                                                formikProps={formik}
                                                showConvertToUserInputCheckbox={true}
                                                onConvertToUserInput={(checked) =>
                                                    persistSelectedToolArgument(arg.key, checked)
                                                }
                                            />
                                        ))}
                                        <ToolArgFormAutoSave
                                            debounceMs={500}
                                            selectedTool={selectedToolWishList?.selected_tool}
                                        />
                                    </form>
                                )}
                            </Formik>

                            {/* Tool Fallback arguments */}
                            <div className="section-header mt-[36px] pb-[6px] mb-[4px]">
                                <h4 className="text-[15px] font-semibold text-blue-200">
                                    Fallback Tools Arguments
                                </h4>
                                {/* <p className="text-[14px] text-neutral-400">Configure the arguments for the fallback tools.</p> */}
                                <Divider className="mt-2" />
                            </div>
                            <Formik
                                innerRef={toolsFallbackArgumentsFormRef}
                                initialValues={{
                                    forms: fallbackToolsArgumentForm
                                }}
                                onSubmit={() => {}}
                            >
                                {(formik) => (
                                    <form onSubmit={formik.handleSubmit}>
                                        <FieldArray name="forms">
                                            {() =>
                                                fallbackToolsArgumentForm!.length > 0 &&
                                                fallbackToolsArgumentForm!.map((tool, index) => (
                                                    <Accordion
                                                        allowToggle
                                                        defaultIndex={[0]}
                                                        key={`fb-tool-${index}`}
                                                        mb={"12px"}
                                                    >
                                                        <AccordionItem
                                                            style={{
                                                                border: "1px solid #555555",
                                                                borderRadius: "8px"
                                                            }}
                                                        >
                                                            <AccordionButton
                                                                pl={"14px"}
                                                                py={"12px"}
                                                                className="flex items-center justify-between"
                                                            >
                                                                <h4 className="text-[15px] font-semibold">
                                                                    <VscTools
                                                                        size={"18px"}
                                                                        className="inline-block"
                                                                    />
                                                                    &nbsp;
                                                                    {tool.display_name ||
                                                                        tool.tool_name}
                                                                </h4>
                                                                <AccordionIcon />
                                                            </AccordionButton>

                                                            <AccordionPanel px={1} pt={0}>
                                                                <div className="pl-[12px] pr-2">
                                                                    {tool.args.map(
                                                                        (arg, _index) => (
                                                                            <FormikFormInputControl
                                                                                key={`formik-control-${_index}`}
                                                                                name={`forms.${index}.args.${_index}.value`}
                                                                                argument={arg}
                                                                                formikProps={formik}
                                                                                showConvertToUserInputCheckbox={
                                                                                    true
                                                                                }
                                                                                onConvertToUserInput={(
                                                                                    checked
                                                                                ) =>
                                                                                    persistFallbackToolArgument(
                                                                                        tool.tool_id,
                                                                                        arg.key,
                                                                                        checked
                                                                                    )
                                                                                }
                                                                            />
                                                                        )
                                                                    )}
                                                                </div>
                                                            </AccordionPanel>
                                                        </AccordionItem>
                                                    </Accordion>
                                                ))
                                            }
                                        </FieldArray>
                                        <FallbackToolsArgumentsAutoSave
                                            debounceMs={500}
                                            selectedToolWishList={selectedToolWishList}
                                        />
                                    </form>
                                )}
                            </Formik>
                        </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem>
                        <AccordionButton className="control-panel-accordion-btn">
                            <Box as="span" flex="1" textAlign="left">
                                <h4 className="text-[15px]">Previous Outputs</h4>
                                <p className="text-[13px] font-normal text-gray-400">
                                    Configure Tool Dependencies and Output Flow Within and Across
                                    Cells
                                </p>
                            </Box>
                            <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel pb={0} px={1}>
                            <Formik
                                initialValues={{
                                    forms: selectedToolWishList?.require_previous_output
                                }} // Add the select field to initial values
                                onSubmit={() => {}}
                            >
                                {({ values }) => (
                                    <Form>
                                        <FieldArray name="forms">
                                            {({ remove, push }) => (
                                                <Box mb={4}>
                                                    <div className="mb-3">
                                                        <Button
                                                            colorScheme="blue"
                                                            size="sm"
                                                            leftIcon={<AddIcon />}
                                                            onClick={() =>
                                                                push({ cell: "", tool: "" })
                                                            }
                                                        >
                                                            Add Previous Output
                                                        </Button>
                                                    </div>

                                                    {values.forms!.length > 0 &&
                                                        values.forms!.map((_item, index) => (
                                                            <Box
                                                                key={`form-box-${index}`}
                                                                className="relative mt-4 mb-4 border-[1px] px-3 pt-4 pb-6 rounded-lg border-zinc-500"
                                                            >
                                                                <div className="flex justify-end absolute right-[12px] top-[12px]">
                                                                    <button
                                                                        onClick={() =>
                                                                            remove(index)
                                                                        }
                                                                        className="text-red-400"
                                                                    >
                                                                        <BsTrash size={"18px"} />
                                                                    </button>
                                                                </div>
                                                                <FormControl mb={4}>
                                                                    <p className="text-[14px] text-white mb-2 font-medium">
                                                                        Cell Identifier
                                                                    </p>
                                                                    <Field
                                                                        as={Input}
                                                                        type="number"
                                                                        name={`forms.${index}.cell`}
                                                                        placeholder="Enter cell Identifier"
                                                                    />
                                                                </FormControl>

                                                                <FormControl>
                                                                    <p className="text-[14px] text-white mb-2 font-medium">
                                                                        Tool Identifier
                                                                    </p>
                                                                    <Field
                                                                        as={Input}
                                                                        type="number"
                                                                        name={`forms.${index}.tool`}
                                                                        placeholder="Enter tool Identifier"
                                                                    />
                                                                </FormControl>
                                                            </Box>
                                                        ))}
                                                </Box>
                                            )}
                                        </FieldArray>
                                        <PreviousOutputAutoSave debounceMs={500} />
                                    </Form>
                                )}
                            </Formik>
                            {/* <h2 className="panel-title">All Capabilities</h2> */}

                            {/* <div className="autocomplete">
                            <div className="input-group">
                                <input type="text" id="tools-search-input" className="input search-input tools-search-input"
                                    placeholder="Search..."
                                    data-auto-list="auto-complete-list" />
                                <Input placeholder="Search tools" />
                            </div>
                            <div id="auto-complete-list" className="autocomplete-list cards-autocomplete-list" data-chips-container="tool-data-chips" data-for="tools-search-input" data-type="tool"></div>
                        </div> */}
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
            </div>

            <ToolsLabControlPanel open={toolTestDisc.isOpen} onClose={toolTestDisc.onClose} />
            {/* Wished tools output view */}
            <ToolWishListOutput
                open={toolWishListTestDisc.isOpen}
                onClose={toolWishListTestDisc.onClose}
                wishedTool={selectedToolWishList as WishedTool}
                outputData={outputData}
            />
            {/* Persona Selecte modal */}
            <Modal isOpen={selectPersonaDisc.isOpen} onClose={selectPersonaDisc.onClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Select Persona</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <div className="min-h-[70px]">
                            <Formik
                                innerRef={formikRef}
                                initialValues={{ persona: "" }}
                                onSubmit={() => {}}
                            >
                                {(formik) => (
                                    <form>
                                        <FormControl
                                            isInvalid={
                                                !!formik.errors.persona && !!formik.touched.persona
                                            }
                                        >
                                            <FormikSelect
                                                name="persona"
                                                placeholder="Select Persona.."
                                                maxMenuHeight={500}
                                                isSearchable={true}
                                                options={personaOptions}
                                                components={{ Option: PersonaSelectOption }}
                                            />
                                            {!!formik.errors.persona &&
                                                !!formik.touched.persona && (
                                                    <p className="form-errors text-[14px] mt-[4px] text-red-300">
                                                        {formik.errors.persona?.toString()}
                                                    </p>
                                                )}
                                        </FormControl>
                                    </form>
                                )}
                            </Formik>
                        </div>
                    </ModalBody>

                    <ModalFooter>
                        <Button
                            colorScheme="gray"
                            variant={"ghost"}
                            mr={3}
                            onClick={selectPersonaDisc.onClose}
                        >
                            Close
                        </Button>
                        <Button background={"#3caf50"} onClick={testWishedTool} color="white">
                            <BsPlay size="22px" />
                            Execute
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </div>
    );
};

function ToolFormAutoSave({ debounceMs, nodeState }) {
    const formikContext = useFormikContext();
    const dispatch = useDispatch();

    // Debounced function to send the request to the server
    const debouncedSave = useCallback(
        debounce(async (values, tools) => {
            const toolWishList = cloneDeep(values);
            const tool = tools.find((tool) => tool.tool_id === values.selected_tool?.value);
            /*
             * Remove the default arguments from the tool list so the app uses the JSON
             */
            toolWishList.selected_tool = tool;
            toolWishList.tool_replacements = values.tool_replacements.map((tool) =>
                nodeState.tools.find((t) => t.tool_id === tool.value)
            );
            // update state
            dispatch(updateSelectedCellToolWishList(toolWishList));
        }, debounceMs),
        []
    );

    // Observe the Formik values and trigger the debounced function on change
    useEffect(() => {
        debouncedSave(formikContext.values, nodeState.tools);
        // Return a cleanup function to cancel debounced calls if component unmounts
        return debouncedSave.cancel;
    }, [formikContext.values, debouncedSave]);

    return null;
}

function ToolArgFormAutoSave({ debounceMs, selectedTool }) {
    const formikContext = useFormikContext();
    const dispatch = useDispatch();

    // Debounced function to send the request to the server
    const debouncedSave = useCallback(
        debounce(async (values, tool) => {
            if (selectedTool) {
                const selectedToolCopy = cloneDeep(tool);

                for (const key of Object.keys(selectedToolCopy.arguments_object)) {
                    if (values[key] === "" || !values[key]) {
                        delete selectedToolCopy.arguments_object[key].default_value;
                        continue;
                    }
                    // check types
                    if (selectedToolCopy.arguments_object[key].type === "number") {
                        selectedToolCopy.arguments_object[key].default_value = Number(
                            values[key].trim()
                        );
                    } else if (selectedToolCopy.arguments_object[key].type === "string") {
                        selectedToolCopy.arguments_object[key].default_value = values[key].trim();
                    } else {
                        selectedToolCopy.arguments_object[key].default_value = values[key];
                    }
                }
                // update state
                dispatch(
                    updateSelectedCellToolWishList({
                        selected_tool: selectedToolCopy
                    })
                );
            }
        }, debounceMs),
        []
    );

    // Observe the Formik values and trigger the debounced function on change
    useEffect(() => {
        debouncedSave(formikContext.values, selectedTool);
        // Return a cleanup function to cancel debounced calls if component unmounts
        return debouncedSave.cancel;
    }, [formikContext.values, debouncedSave]);

    return null;
}

function PreviousOutputAutoSave({ debounceMs }) {
    const formikContext = useFormikContext();
    const dispatch = useDispatch();

    // Debounced function to send the request to the server
    const debouncedSave = useCallback(
        debounce(async (values) => {
            dispatch(
                updateSelectedCellToolWishList({
                    require_previous_output: values.forms
                })
            );
        }, debounceMs),
        []
    );

    // Observe the Formik values and trigger the debounced function on change
    useEffect(() => {
        debouncedSave(formikContext.values);
        // Return a cleanup function to cancel debounced calls if component unmounts
        return debouncedSave.cancel;
    }, [formikContext.values, debouncedSave]);

    return null;
}

function FallbackToolsArgumentsAutoSave({ debounceMs, selectedToolWishList }) {
    const formikContext = useFormikContext();
    const dispatch = useDispatch();

    const debouncedSave = useCallback(
        debounce(async (values, toolWishlist) => {
            const selectedTestToolCopy: WishedTool = cloneDeep(toolWishlist);

            const tool_replacements = selectedTestToolCopy.tool_replacements.map((tool) => {
                /**
                 * For every tool, replace with the args of the form
                 */
                const modifiedTool = values.forms.find((v) => v.tool_id === tool.tool_id);

                if (modifiedTool) {
                    // loop through arguments an replace values
                    modifiedTool.args.forEach((arg) => {
                        // Exit if the value is undefined
                        if (!arg.value) {
                            return false;
                        }

                        tool.arguments_object[arg.key].default_value = arg.value;
                    });
                }

                return tool;
            });

            dispatch(
                updateSelectedCellToolWishList({
                    tool_replacements
                })
            );
        }, debounceMs),
        []
    );

    // Observe the Formik values and trigger the debounced function on change
    useEffect(() => {
        debouncedSave(formikContext.values, selectedToolWishList);
        // Return a cleanup function to cancel debounced calls if component unmounts
        return debouncedSave.cancel;
    }, [formikContext.values, debouncedSave]);

    return null;
}

export default ToolsControlPanel;
